import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import moment from 'moment';
import { TriageModel } from 'data/api/triage.api';
import {
  latestOutboundNote,
  plainText,
  recipientNames,
  triageCreatedAt,
} from 'utils/triage';
import { CARD } from './EventDetails';

// Muted, and distinct from each other at a glance without competing with the
// escalation chip the Assignment section above already uses.
const STATUS_COLOURS: { [status: string]: { bg: string; fg: string } } = {
  New: { bg: '#EAF1F8', fg: '#213B54' },
  'In Progress': { bg: '#FDF3E4', fg: '#8A5300' },
  Closed: { bg: '#EFEDEB', fg: '#7D818C' },
};

const FALLBACK_COLOURS = { bg: '#EFEDEB', fg: '#7D818C' };

export const statusColours = (status?: string) =>
  STATUS_COLOURS[status || ''] || FALLBACK_COLOURS;

export const TriageStatusChip: React.FC<{ status?: string }> = ({ status }) => {
  const colours = statusColours(status);
  return (
    <Chip
      label={status || 'Unknown'}
      size="small"
      sx={{
        backgroundColor: colours.bg,
        color: colours.fg,
        fontSize: '13px',
        fontWeight: 600,
        height: '22px',
      }}
    />
  );
};

export const EmailFailedChip: React.FC = () => (
  <Chip
    label="Email failed"
    size="small"
    sx={{
      backgroundColor: '#FEF3F2',
      color: '#B42318',
      fontSize: '13px',
      fontWeight: 600,
      height: '22px',
    }}
  />
);

const clampToLines = (lines: number) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
});

interface ITriageCardProps {
  triage: TriageModel;
  onOpen: (triagesId: string) => void;
}

export const TriageCard: React.FC<ITriageCardProps> = ({ triage, onOpen }) => {
  const to = recipientNames(triage, 'to');
  const snippet = plainText(triage.notes?.[0]?.note);
  const noteCount = triage.notes?.length || 0;
  const emailFailed = latestOutboundNote(triage)?.emailStatus === 'failed';
  const open = () => onOpen(triage.triagesId);

  return (
    <Stack
      role="button"
      tabIndex={0}
      data-focus-key={triage.triagesId}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      sx={{
        ...CARD,
        padding: '10px 12px',
        gap: '6px',
        cursor: 'pointer',
        ':hover': { borderColor: '#356766' },
        ':focus-visible': {
          outline: '2px solid #356766',
          outlineOffset: '2px',
        },
      }}
    >
      <Stack direction="row" alignItems="baseline" gap={1}>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '20px',
            color: '#213B54',
            ...clampToLines(2),
          }}
        >
          {triage.subject || triage.triageOptionsType || 'Triage'}
        </Typography>
        <Typography
          sx={{ flex: 'none', fontSize: '13px', color: '#7D818C' }}
          title={moment(triageCreatedAt(triage)).format('MM/DD/YYYY hh:mm A')}
        >
          {moment(triageCreatedAt(triage)).format('MMM D, YYYY')}
        </Typography>
      </Stack>

      {!!to.length && (
        <Typography
          title={to.join(', ')}
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: '#213B54',
            ...clampToLines(1),
          }}
        >
          To {to.join(', ')}
        </Typography>
      )}

      {!!snippet && (
        <Typography
          sx={{
            fontSize: '13px',
            lineHeight: '18px',
            color: '#7D818C',
            ...clampToLines(2),
          }}
        >
          {snippet}
        </Typography>
      )}

      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        sx={{ flexWrap: 'wrap' }}
      >
        <TriageStatusChip status={triage.triagesStatus} />
        {emailFailed && <EmailFailedChip />}
        <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
          {noteCount} {noteCount === 1 ? 'message' : 'messages'}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ marginLeft: 'auto', color: '#356766' }}
        >
          <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
            Open
          </Typography>
          <ChevronRightRoundedIcon sx={{ fontSize: '18px' }} />
        </Stack>
      </Stack>
    </Stack>
  );
};

// The compact form an older triage takes once the list is expanded: enough to
// recognise it and open it, and never so compact that a failed email hides.
export const TriageRow: React.FC<ITriageCardProps & { divider?: boolean }> = ({
  triage,
  onOpen,
  divider,
}) => {
  const to = recipientNames(triage, 'to');
  const emailFailed = latestOutboundNote(triage)?.emailStatus === 'failed';
  const open = () => onOpen(triage.triagesId);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      role="button"
      tabIndex={0}
      data-focus-key={triage.triagesId}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      sx={{
        padding: '10px 12px',
        cursor: 'pointer',
        borderTop: divider ? '1px solid #EFEDEB' : undefined,
        ':hover': { backgroundColor: '#F3F7F7' },
        ':focus-visible': {
          outline: '2px solid #356766',
          outlineOffset: '-2px',
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          flex: 'none',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: emailFailed
            ? '#B42318'
            : statusColours(triage.triagesStatus).fg,
        }}
      />
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '20px',
            color: '#213B54',
            ...clampToLines(1),
          }}
        >
          {triage.subject || triage.triageOptionsType || 'Triage'}
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            lineHeight: '18px',
            color: emailFailed ? '#B42318' : '#7D818C',
            ...clampToLines(1),
          }}
        >
          {[emailFailed ? 'Email failed' : triage.triagesStatus, to.join(', ')]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </Stack>
      <Typography
        sx={{ flex: 'none', fontSize: '13px', color: '#7D818C' }}
        title={moment(triageCreatedAt(triage)).format('MM/DD/YYYY hh:mm A')}
      >
        {moment(triageCreatedAt(triage)).format('MMM D')}
      </Typography>
    </Stack>
  );
};
